import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IResponse } from 'types';
import React from 'react';
import { callEndpoint } from 'redux/global';
import { RootState } from 'redux/store';

export type Metric = {
  startDate: Date,
  endDate: Date
}
export type ModalParts = {
  component: React.JSX.Element,
  exitOnBgClick?: Function,
  noBg?: boolean,
  skipSideBar?: true
  /** Wider right panel — used by manager records access. */
  wide?: boolean
}

export type BranchPosLock = {
  email: string
  username?: string
  screen: 'sales' | 'refunds'
  /** ISO timestamp — lock is free after this without a heartbeat. */
  expiresAt: string
}

export type Branch = {
  name: string,
  staff: string[],
  /** Soft-deleted: hidden from Departments UI, kept for activity history. */
  isDeleted?: boolean,
  /** Exclusive Sales/Refunds session for this department (ephemeral). */
  posLock?: BranchPosLock | null,
}

export type InventoryItem = {
  id: string,
  name: string,
  price: number,
  type: string,
  description?: string,
  unit?: string,
  image?: string,
  /** Optional product barcode / UPC / EAN for scanner selection. */
  barcode?: string,
  /** When true, item is an in-house expense (not sold to guests). */
  isInhouseExpense?: boolean,
  /** Units on hand for guest-sellable items. */
  stock?: number,
  createdAt: string,
  updatedAt: string,
}

/** Expense / in-house items: flagged or stored with a negative price. */
export const isExpenseInventoryItem = (item: Pick<InventoryItem, 'price' | 'isInhouseExpense'>) => (
  Boolean(item.isInhouseExpense) || Number(item.price) < 0
);

type SubscriptionPlan = {
  organizationId: string,
  planId: string,
  status: string,
  planTitle: string,
  nextPaymentDate: string,
  createdAt: string,
  paystack_subscription: any
}

export type OrganizationPrefs = {
  /** Named inventory categories available even when no items use them yet. */
  inventoryTypes?: string[];
  /**
   * Inventory group name → department names that may sell/use it.
   * Missing key or empty array means all departments.
   */
  inventoryTypeDepartments?: Record<string, string[]>;
};

export type ImageCredit = {
  balance: number;
  created_as_of: string;
};

/** Command Center / OpenAI credit pack (USD balance). Shared across the org. */
export type AiCredit = {
  balanceUsd: number;
  created_as_of: string;
  source?: 'signup_bonus' | 'subscription' | 'renewal';
  paystack_invoice_id?: string;
};

export type OrganizationData = {
  id: string,
  name: string,
  password: string,
  nameSave?: string,
  address: string,
  email: string,
  username?: string,
  linkedin?: string | null,
  facebook?: string | null,
  instagram?: string | null,
  twitter?: string | null,
  phoneNumber: string,
  logo?: string,
  accNumber?: string | null,
  accName?: string | null,
  field1?: string | null,
  field2?: string | null,
  currency?: string | null,
  displayEmail?: string,
  prefs: OrganizationPrefs,
  branches: Branch[],
  inventory?: InventoryItem[],
  fields: object,
  deal: string,
  country: string,
  region: string,
  branch: string,
  expires?: string,
  billingDate?: string,
  suffix?: string | null,
  city: string,
  coId?: string,
  channelExpiry?: string,
  keyCardUrl?: string,
  keyCardExpiry?: string,
  roomPlan?: string,
  roomSubscription?: SubscriptionPlan,
  channelManagerSubscription?: SubscriptionPlan,
  channelManagerPlan?: string,
  logsPlan?: string,
  logsSubscription?: SubscriptionPlan,
  maxLogs?: number | null,
  storagePlan?: string,
  storageSubscription?: SubscriptionPlan,
  maxStorage?: number | null,
  /** activity_logs COUNT(*) from the hotel DB — not the local /logs page. */
  activityLogsCount?: number | null,
  /** photos COUNT(*) from the hotel DB — includes form uploads and GCS-backed images. */
  photosCount?: number | null,
  /** All login seats (CEOs + employee-register mirrors). */
  staffSeatCount?: number | null,
  seatsPlan?: string,
  seatsSubscription?: SubscriptionPlan,
  maxStaff?: number | null,
  maxEmailCampaigns?: number;
  /** AI inventory image packs; each entry is a purchased credit pack. */
  imageCredits?: ImageCredit[];
  /** Command Center OpenAI credits (USD); owner subscription tops up for all staff. */
  aiCredits?: AiCredit[];
  aiCreditsPlan?: string;
  aiCreditsSubscription?: SubscriptionPlan;
  modifiedAt?: string;
}

export interface ViewsState {
  socket: any,
  isSocketConnected: boolean,
  metricTime: Metric | null,
  debugText: any[],
  organizationData: OrganizationData | null,
  modalParts: ModalParts | null,
  isShowSideBar: string[],
  rightBarDismissTick: number,
}

const oneMonthAgo = new Date();
oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
oneMonthAgo.setHours(0, 0, 0, 0);
const lateEndD = new Date();
lateEndD.setHours(23, 59, 59, 999);

const initialState: ViewsState = {
  socket: null,
  isSocketConnected: true,
  metricTime: {
    startDate: oneMonthAgo,
    endDate: lateEndD,
  },
  debugText: [],
  organizationData: null,
  modalParts: null,
  isShowSideBar: [],
  rightBarDismissTick: 0,
};

export const counterSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    setSocket: (state, action) => {
      state.socket = action.payload;
    },
    setIsSocketConnected: (state, action: PayloadAction<boolean>) => {
      state.isSocketConnected = action.payload;
    },
    setMetricTime: (state, action: PayloadAction<Metric | null>) => {
      state.metricTime = action.payload;
    },
    setDebugText: (state, action: PayloadAction<any[]>) => {
      state.debugText = action.payload;
    },
    setOrganizationData: (state, action: PayloadAction<OrganizationData | null>) => {
      if (action.payload) {
        localStorage.setItem('organization_data_id', action.payload.id.toString());
      } else {
        localStorage.removeItem('organization_data_id');
      }
      state.organizationData = action.payload;
    },
    setModalParts: (state, action: PayloadAction<ModalParts | null>) => {
      state.modalParts = action.payload;
    },
    setIsShowSideBar: (state, action: PayloadAction<boolean | string[]>) => {
      if (typeof action.payload === 'boolean') {
        state.isShowSideBar = action.payload ? ['main'] : [];
      } else {
        state.isShowSideBar = action.payload;
      }
    },
    addIsShowSideBarKey: (state, action: PayloadAction<string>) => {
      if (!state.isShowSideBar.includes(action.payload)) {
        state.isShowSideBar.push(action.payload);
      }
    },
    removeIsShowSideBarKey: (state, action: PayloadAction<string>) => {
      state.isShowSideBar = state.isShowSideBar.filter((item) => item !== action.payload);
    },
    bumpRightBarDismiss: (state) => {
      state.rightBarDismissTick += 1;
    },
  },
});

export const {
  setSocket,
  setIsSocketConnected,
  setMetricTime,
  setDebugText,
  setOrganizationData,
  setModalParts,
  setIsShowSideBar,
  addIsShowSideBarKey,
  removeIsShowSideBarKey,
  bumpRightBarDismiss,
} = counterSlice.actions;

export const selectSocket = (state: RootState) => state.views.socket;
export const selectIsSocketConnected = (state: RootState) => state.views.isSocketConnected;
export const selectMetricTime = (state: RootState) => state.views.metricTime;
export const selectDebugText = (state: RootState) => state.views.debugText;
export const selectOrganizationData = (state: RootState) => state.views.organizationData;
export const selectModalParts = (state: RootState) => state.views.modalParts;
/** Org display currency when set; else $. Settings subscription plans keep their own USD/₦ amounts. */
export const selectCurrency = (state: RootState) => state.views.organizationData?.currency || '$';
export const selectIsShowSideBar = (state: RootState) => state.views.isShowSideBar;
export const selectRightBarDismissTick = (state: RootState) => state.views.rightBarDismissTick;

export default counterSlice.reducer;

export const setBranches = (branches: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'setbranches',
      method: 'POST',
      body: { branches }
    }),
  );
  return res;
};

export const claimPosLock = (
  department: string,
  screen: 'sales' | 'refunds',
) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'claimposlock',
      method: 'POST',
      body: { department, screen },
    }),
  );
  return res;
};

export const heartbeatPosLock = (department: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'heartbeatposlock',
      method: 'POST',
      body: { department },
    }),
  );
  return res;
};

export const releasePosLock = (department: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'releaseposlock',
      method: 'POST',
      body: { department },
    }),
  );
  return res;
};

export const setInventory = (inventory: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'setinventory',
      method: 'POST',
      body: { inventory }
    }),
  );
  return res;
};

export const addTmpOrganization = (organizationData: OrganizationData) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addTMPorganization',
      method: 'POST',
      body: { organizationData: organizationData }
    }),
  );
  return res;
};

export const transferTmpOrganization = (id: string) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'transferTMPorganization',
      method: 'POST',
      body: { id }
    }),
  );
  return res;
};

